import React, { useCallback, useRef, useState, useEffect } from 'react';
import { FaFileUpload } from 'react-icons/fa';
import { FaCamera, FaUpload } from 'react-icons/fa6';

type Props = {
  onFileSelected: (file: File | null) => void;
  onError?: (message: string) => void;
  accept?: string;                // input accept string; defaults to image/*
  allowedMimeTypes?: string[];    // explicit allow-list
  capture?: 'user' | 'environment';
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  showPreview?: boolean;
  maxSizeMB?: number;             // defaults to 10MB
};

const DEFAULT_ALLOWED = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, num = bytes;
  while (num >= 1024 && i < units.length - 1) { num /= 1024; i++; }
  return `${num.toFixed(num < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const FileUpload: React.FC<Props> = ({
  onFileSelected,
  onError,
  accept = 'image/*',
  allowedMimeTypes = DEFAULT_ALLOWED,
  capture,
  multiple = false,
  disabled = false,
  className = '',
  placeholder = 'Drag & drop or tap to upload',
  showPreview = true,
  maxSizeMB = 10,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const openCamera = useCallback(() => {
    if (disabled) return;
    cameraInputRef.current?.click();
  }, [disabled]);

  const emitError = (msg: string) => {
    setError(msg);
    onError?.(msg);
  };

  const clearError = () => setError(null);

  const validateFile = (file: File): boolean => {
    clearError();

    // Size check
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      emitError(`File is too large: ${formatBytes(file.size)} (max ${maxSizeMB} MB).`);
      return false;
    }

    // Type check (some mobile browsers may give empty type)
    const type = (file.type || '').toLowerCase();
    const name = file.name?.toLowerCase() || '';
    const extOK = /\.(jpe?g|png|webp|heic|heif)$/i.test(name);

    if (!type && extOK) {
      return true; // accept based on extension when mime is missing
    }

    const ok = allowedMimeTypes.some(t => t.toLowerCase() === type);
    if (!ok) {
      emitError(`Unsupported file type: ${type || 'unknown'}. Allowed: ${allowedMimeTypes.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) {
        onFileSelected(null);
        setPreviewUrl(null);
        return;
      }

      const list: File[] = Array.isArray(files) ? files : Array.from(files);
      if (list.length === 0) {
        onFileSelected(null);
        setPreviewUrl(null);
        return;
      }

      const file = list[0]; // single-file flow
      if (!validateFile(file)) {
        onFileSelected(null);
        setPreviewUrl(null);
        return;
      }

      onFileSelected(file);

      if (showPreview && (file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name))) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } else {
        setPreviewUrl(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFileSelected, showPreview, maxSizeMB, allowedMimeTypes]
  );

  // drag & drop (desktop)
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  }, [disabled]);

  const onDragLeave = useCallback(() => setDragging(false), []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }, [disabled, handleFiles]);

  useEffect(() => {
    // SSR guard
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const onPaste = (e: Event) => {
      if (disabled) return;

      const ce = e as ClipboardEvent;
      const dt = ce.clipboardData;
      if (!dt) return;

      // Prefer files if available
      if (dt.files && dt.files.length > 0) {
        handleFiles(dt.files);
        return;
      }

      // Fallback: scan items for file blobs
      if (dt.items && dt.items.length > 0) {
        const files: File[] = [];
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (item.kind === 'file') {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
        if (files.length > 0) handleFiles(files);
      }
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [disabled, handleFiles]);

  // cleanup preview URLs
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="w-100 h-100">
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`border rounded d-flex flex-column align-items-center justify-content-center p-3 w-100 h-100 ${isDragging ? 'border-primary bg-light' : 'border-secondary'
          } ${disabled ? 'opacity-50' : ''} ${className}`}
        style={{ minHeight: 140 }}
      >

        <div className="text-muted text-center mb-3">
          <strong>{placeholder}</strong>
          <div className="small mt-1">Choose from camera or files</div>
        </div>

        {/* Two separate buttons for camera and files */}
        <div className="d-flex gap-2 mb-2">
          <button
            type="button"
            onClick={openCamera}
            disabled={disabled}
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            style={{ fontSize: '0.9rem' }}
          >
            <FaCamera /> Camera
          </button>
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled}
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            style={{ fontSize: '0.9rem' }}
          >
            <FaUpload /> Files
          </button>
        </div>

        {showPreview && previewUrl && (
          <img
            src={previewUrl}
            alt="Preview"
            className="img-fluid rounded mt-3"
            style={{ maxHeight: 180, objectFit: 'contain' }}
          />
        )}

        {/* File input - for gallery/files */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />

        {/* Camera input - forces camera */}
        <input
          ref={cameraInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          capture="environment"
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      {/* Error message */}
      <div role="alert" aria-live="polite" className="mt-2">
        {error && <div className="text-danger small">{error}</div>}
      </div>
    </div>
  );
};

export default FileUpload;
